import { defineStore } from 'pinia';
import * as t from 'io-ts';
import { DateFromUnixTime } from 'io-ts-types/lib/DateFromUnixTime';
import { computed, ref } from 'vue';
import fromEnum from '@/scripts/io-ts/fromEnum';
import { Theme } from '@/scripts/constants/themes';
import type { UserGroup } from '@/scripts/types/UserGroup';
import { UserGroupC } from '@/scripts/types/UserGroup';
import { useTimerStore } from '@/vue/stores/timer';

export const PasswordComplexityC = t.union([
    t.type({
        enabled: t.literal(true),
        expiredAt: t.union([DateFromUnixTime, t.null]),
    }),
    t.type({
        enabled: t.literal(false),
    }),
]);

export type PasswordComplexity = t.TypeOf<typeof PasswordComplexityC>;

export const UserDetailsC = t.type({
    userName: t.string,
    inGlobalContainer: t.boolean,
    theme: fromEnum('Theme', Theme),
    userId: t.number,
    userGroupsIds: t.array(t.number),
    userGroup: UserGroupC,
    userAvatarFilePath: t.union([t.string, t.null]),
    passwordComplexity: PasswordComplexityC,
    autoPauseRecordWhenComment: t.boolean,
    autoPlayAudioComments: t.boolean,
    notificationSound: t.boolean,
});

export type UserDetails = t.TypeOf<typeof UserDetailsC>;

const PASSWORD_EXPIRED_TIMER = 'password-expiration';

const getDefaultTheme = () => {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (isDark) {
        return Theme.DARK;
    }

    return Theme.DEFAULT;
};

export const useUserStore = defineStore('user', () => {
    const permissionsList = ref<string[] | null>(null);
    const userName = ref<string | null>(null);
    const userId = ref<number | null>(null);
    const userGroupsIds = ref<number[]>([]);
    const userGroup = ref<UserGroup | null>(null);
    const userAvatarPath = ref<string | null>(null);
    const passwordComplexity = ref<PasswordComplexity | null>(null);
    const isAutoPauseRecordWhenComment = ref(true);
    const isAutoPlayAudioComments = ref(true);
    const isNotificationSoundEnabled = ref(false);
    const inGlobalContainer = ref(false);
    const theme = ref<Theme>(getDefaultTheme());

    const permissions = computed<string[]>(() => permissionsList.value ?? []);
    const arePermissionsSet = computed(() => permissionsList.value !== null);
    const isUserLoggedIn = computed(() => userId.value !== null);

    const isPasswordExpired = computed(() => {
        if (
            !passwordComplexity.value
            || !passwordComplexity.value.enabled
            || passwordComplexity.value.expiredAt === null
        ) {
            return false;
        }

        return useTimerStore().now >= passwordComplexity.value.expiredAt;
    });

    const hasPermission = (name: string): boolean => permissions.value.includes(name);
    const hasSomePermission = (names: string[]): boolean => names.some(hasPermission);
    const hasEveryPermission = (names: string[]): boolean => names.every(hasPermission);

    const setPermissions = (value: string[]): void => {
        permissionsList.value = value;
    };

    const reset = (): void => {
        permissionsList.value = null;
        userName.value = null;
        userId.value = null;
        userAvatarPath.value = null;
        passwordComplexity.value = null;
        userGroup.value = null;
    };

    const setUserDetails = (details: UserDetails): void => {
        userGroup.value = details.userGroup;
        userName.value = details.userName;
        userId.value = details.userId;
        userGroupsIds.value = details.userGroupsIds;
        userAvatarPath.value = details.userAvatarFilePath;
        passwordComplexity.value = details.passwordComplexity;
        isAutoPauseRecordWhenComment.value = details.autoPauseRecordWhenComment;
        isAutoPlayAudioComments.value = details.autoPlayAudioComments;
        isNotificationSoundEnabled.value = details.notificationSound;
        inGlobalContainer.value = details.inGlobalContainer;
        theme.value = details.theme;

        const timerStore = useTimerStore();
        if (passwordComplexity.value?.enabled) {
            timerStore.startTimer(PASSWORD_EXPIRED_TIMER);
        } else {
            timerStore.destroyTimer(PASSWORD_EXPIRED_TIMER);
        }
    };

    return {
        userName,
        userId,
        userGroupsIds,
        userGroup,
        userAvatarPath,
        passwordComplexity,
        isAutoPauseRecordWhenComment,
        isAutoPlayAudioComments,
        isNotificationSoundEnabled,
        inGlobalContainer,
        theme,
        permissions,
        arePermissionsSet,
        isUserLoggedIn,
        isPasswordExpired,
        hasPermission,
        hasSomePermission,
        hasEveryPermission,
        setPermissions,
        reset,
        setUserDetails,
    };
});
